import mapboxgl from 'mapbox-gl';
import { Root, createRoot } from 'react-dom/client';
import { Box } from '@mui/material';
import { ICoordinateComments } from '../../core/interfaces/coordinate-comments.interface';
import { ConfirmDeleteDialog } from './ConfirmDelete';
import { AddCommentComponent, CloseCommentsComponent } from './CommentControlUI';
import { createMarkerElement, parseCoordinate, createPopupContentFromComment } from './CommentControlHelpers';

export class CommentControl implements mapboxgl.IControl {
  private container: HTMLElement;
  private root: Root;
  private map: mapboxgl.Map;
  private markers: mapboxgl.Marker[] = [];
  private markersVisible = true;
  private onUpdate?: (coordinateComments: ICoordinateComments) => void;
  private coordinateComment?: ICoordinateComments[];
  private onDelete?: (coordinateComments: ICoordinateComments) => void;
  private deleteDialogOpen = false;
  private commentToDelete: ICoordinateComments | null = null;
  private markerToDelete: mapboxgl.Marker | null = null;
  private isAddingComment = false;
  private activePopup: mapboxgl.Popup | null = null;

  constructor(
    private readonly habitatOptions = [],
    onUpdate: (coordinateComments: ICoordinateComments) => void,
    onDelete: (coordinateComments: ICoordinateComments) => void,
    coordinateComment: ICoordinateComments[],
  ) {
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl';
    this.container.id = 'comment-control';
    this.root = createRoot(this.container);
    this.onUpdate = onUpdate;
    this.onDelete = onDelete;
    this.coordinateComment = coordinateComment;

    this.render();
  }

  update(coordinateComment: ICoordinateComments[]) {
    this.coordinateComment = coordinateComment;
    this.createMarkersFromComments();
    this.render();
  }

  onAdd(map) {
    this.map = map;
    this.createMarkersFromComments();
    return this.container;
  }

  private createMarkersFromComments() {
    this.clearAllMarkers();
    if (!this.coordinateComment?.length) return;

    this.coordinateComment.forEach((comment) => {
      const lngLat = parseCoordinate(comment.coordinate);
      if (lngLat) {
        const markerElement = createMarkerElement(comment, () => this.showCommentPopup(comment));
        const marker = new mapboxgl.Marker(markerElement).setLngLat(lngLat).addTo(this.map);
        this.markers.push(marker);
      }
    });
  }

  private showCommentPopup(comment: ICoordinateComments) {
    this.activePopup?.remove();

    const lngLat = parseCoordinate(comment.coordinate);
    if (!lngLat) return;

    const popupContent = createPopupContentFromComment(comment, {
      onDelete: () => this.handleDelete(comment),
      onSave: (text: string) => {
        // update comment text and notify parent
        comment.comment = text;
        this.onUpdate?.(comment);
        this.activePopup?.remove();
      },
    });

    this.activePopup = new mapboxgl.Popup({
      closeOnClick: true,
      closeButton: true,
      offset: 10,
      className: 'comment-popup',
    })
      .setLngLat(lngLat)
      .setDOMContent(popupContent)
      .addTo(this.map);

    this.activePopup.on('close', () => {
      this.activePopup = null;
    });
  }

  private handleDelete(comment: ICoordinateComments) {
    this.commentToDelete = comment;
    const markerIndex = this.coordinateComment?.findIndex((c) => c === comment) ?? -1;
    if (markerIndex >= 0) {
      this.markerToDelete = this.markers[markerIndex];
    }
    this.deleteDialogOpen = true;
    this.activePopup?.remove();
    this.render();
  }

  render() {
    this.root.render(
      <Box>
        <AddCommentComponent onAddComment={() => this.onAddComment()} />
        <CloseCommentsComponent onCloseComments={() => this.toggleMarkers()} />
        <ConfirmDeleteDialog
          open={this.deleteDialogOpen}
          onClose={(confirmed) => this.handleDeleteConfirmation(confirmed)}
        />
      </Box>,
    );
  }

  private handleDeleteConfirmation(confirmed: boolean) {
    if (confirmed && this.commentToDelete) {
      this.coordinateComment = this.coordinateComment?.filter((comment) => comment !== this.commentToDelete);
      this.markerToDelete?.remove();
      this.markers = this.markers.filter((marker) => marker !== this.markerToDelete);
      this.onDelete?.(this.commentToDelete);
      this.createMarkersFromComments();
    }
    this.deleteDialogOpen = false;
    this.commentToDelete = null;
    this.markerToDelete = null;
    this.render();
  }

  onAddComment() {
    this.isAddingComment = true;
    this.container.classList.add('active');
    this.map?.on('mousedown', this.handleMouseDown);
  }

  private handleMouseDown = (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
    if (!this.markersVisible) return;

    this.activePopup?.remove();

    const tempPopup = new mapboxgl.Popup({
      closeOnClick: false,
      closeButton: true,
      className: 'comment-popup',
    }).setLngLat(e.lngLat);

    const popupContent = createPopupContentFromComment(
      { comment: '', coordinate: e.lngLat, _isNew: true } as any,
      {
        onCancel: () => {
          this.isAddingComment = false;
          this.container.classList.remove('active');
          tempPopup.remove();
        },
        onSave: (text: string) => {
          // create a new comment object and notify parent
          const newComment: ICoordinateComments = {
            comment: text,
            coordinate: e.lngLat,
          } as ICoordinateComments;
          this.coordinateComment = [...(this.coordinateComment || []), newComment];
          this.onUpdate?.(newComment);
          tempPopup.remove();
        },
      },
    );

    tempPopup.setDOMContent(popupContent).addTo(this.map);

    setTimeout(() => {
      this.map.off('mousedown', this.handleMouseDown);
      this.isAddingComment = false;
      this.container.classList.remove('active');
    }, 0);
  };

  toggleMarkers() {
    this.markersVisible ? this.hideAllMarkers() : this.showAllMarkers();
  }

  hideAllMarkers() {
    this.markers.forEach((marker) => marker.remove());
    this.markersVisible = false;
    this.activePopup?.remove();
  }

  showAllMarkers() {
    this.markersVisible = true;
    this.createMarkersFromComments();
  }

  clearAllMarkers() {
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
    this.activePopup?.remove();
  }

  onRemove() {
    this.clearAllMarkers();
    this.container.parentNode?.removeChild(this.container);
    this.map = undefined;
  }

  isActive(): boolean {
    return this.isAddingComment;
  }
}
